/**
 * AddToProjectButton Component
 * ============================
 * Dropdown button to add material to an existing project or create new.
 * Works in both DEMO MODE (localStorage) and AUTH MODE (Supabase).
 */

import React, { useState, useEffect, useRef } from 'react';
import { FolderPlus, Plus, Check, ChevronDown } from 'lucide-react';
import { useBlueprint } from '../../context/BlueprintContext';
import { getProjects, addMaterialToProject, createProject } from '../../services/workspaceService';

const AddToProjectButton = ({ 
  materialId, 
  materialData = {},
  variant = 'button', // 'button' | 'icon'
  size = 'md',
  className = '',
  onAdd
}) => {
  const { getContent } = useBlueprint();
  const [isOpen, setIsOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const dropdownRef = useRef(null);
  
  // Labels from Blueprint
  const addToProjectLabel = getContent('actions.add_to_project', 'Aggiungi a progetto');
  const newProjectLabel = getContent('actions.new_project', 'Nuovo progetto');
  const createLabel = getContent('actions.create', 'Crea');
  
  // Load projects when dropdown opens (works for both demo and auth)
  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);
  
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowNewProject(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadProjects = async () => {
    setLoading(true);
    const data = await getProjects();
    setProjects(data);
    setLoading(false);
  };
  
  // NO LOGIN REQUIRED - Works in demo mode
  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };
  
  const handleAddToProject = async (projectId) => {
    setLoading(true);
    const result = await addMaterialToProject(projectId, materialId, materialData);
    
    if (result.success) {
      // Brief feedback
      setTimeout(() => {
        setIsOpen(false);
        if (onAdd) onAdd(result);
      }, 500);
    }
    
    setLoading(false);
  };
  
  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;
    
    setLoading(true);
    const result = await createProject({ name: newProjectName.trim() });
    
    if (result.success) {
      // Add material to new project
      await addMaterialToProject(result.data.id, materialId, materialData);
      setNewProjectName('');
      setShowNewProject(false);
      await loadProjects();
      
      setTimeout(() => {
        setIsOpen(false);
        if (onAdd) onAdd(result);
      }, 500);
    }
    
    setLoading(false);
  };
  
  const sizes = {
    sm: { icon: 14, padding: 'p-1.5' },
    md: { icon: 16, padding: 'p-2' },
    lg: { icon: 20, padding: 'p-2.5' }
  };
  
  const { icon: iconSize, padding } = sizes[size] || sizes.md;
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      {variant === 'icon' ? (
        <button
          onClick={handleToggle}
          className={`${padding} rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-all ${className}`}
          title={addToProjectLabel}
          data-testid="add-to-project-icon"
        >
          <FolderPlus size={iconSize} />
        </button>
      ) : (
        <button
          onClick={handleToggle}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all bg-[#1A1A18] text-white border border-[#3A3A38] hover:border-[var(--gold)] ${className}`}
          data-testid="add-to-project-button"
        >
          <FolderPlus size={iconSize} />
          <span>{addToProjectLabel}</span>
          <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      )}
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-[#1A1A18] border border-[#3A3A38] rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Projects List */}
          <div className="max-h-48 overflow-y-auto">
            {loading && projects.length === 0 ? (
              <div className="px-4 py-3 text-[#888] text-sm">Loading...</div>
            ) : projects.length === 0 ? (
              <div className="px-4 py-3 text-[#888] text-sm">Nessun progetto</div>
            ) : (
              projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleAddToProject(project.id)}
                  className="w-full px-4 py-3 text-left hover:bg-[#2A2A28] transition-colors flex items-center justify-between group"
                  disabled={loading}
                >
                  <div>
                    <p className="text-white text-sm font-medium">{project.name}</p>
                    <p className="text-[#666] text-xs">
                      {project.project_materials?.length || 0} materiali
                    </p>
                  </div>
                  <Plus size={16} className="text-[#666] group-hover:text-[var(--gold)] transition-colors" />
                </button>
              ))
            )}
          </div>
          
          {/* Divider */}
          <div className="border-t border-[#3A3A38]" />
          
          {/* New Project */}
          {showNewProject ? (
            <form onSubmit={handleCreateProject} className="p-3">
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Nome progetto"
                className="w-full px-3 py-2 bg-[#0A0A09] border border-[#3A3A38] rounded-lg text-white text-sm placeholder-[#666] focus:border-[var(--gold)] focus:outline-none mb-2"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewProject(false)}
                  className="flex-1 px-3 py-1.5 text-[#888] text-sm hover:text-white transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={!newProjectName.trim() || loading}
                  className="flex-1 px-3 py-1.5 bg-[var(--gold)] text-white text-sm rounded-lg font-medium disabled:opacity-50"
                >
                  {createLabel}
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowNewProject(true)}
              className="w-full px-4 py-3 text-left hover:bg-[#2A2A28] transition-colors flex items-center gap-2 text-[var(--gold)]"
            >
              <Plus size={16} />
              <span className="text-sm font-medium">{newProjectLabel}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AddToProjectButton;
